import type { RailItem } from "@/components/shell/PosRail";
import type { OrderType } from "@/lib/api/types";

/**
 * Pack registry — the one place that knows which business verticals exist and what each
 * one's counter navigation is.
 *
 * Why this exists: a POS tenant runs ONE vertical, and the rail must reflect it (a hotel
 * front desk has no "menu"; a clinic has patients, not tables). Screens declare
 * `<PosShell pack="hotel" active="rooms">` and the shell resolves the nav from here, so no
 * screen hand-rolls its own navigation and no rail item is a dead end.
 *
 * Entitlement is server-owned (ADR-0014 pack manifest) and now actually enforced: the shell reads
 * the tenant's entitled packs from /pos/context and opens the one this business runs. The
 * prototype's switcher — which let anyone walk every vertical from the rail — is gone.
 * Unentitled packs are HIDDEN, never shown-disabled.
 */

export type PackId =
  // core packs — each owns purpose-built screens
  | "restaurant"
  | "retail"
  | "salon"
  | "clinic"
  | "diagnostics"
  | "gym"
  | "hotel"
  // Perksmith Rewards at a counter that keeps its own POS (W6, reward-desk-pack-plan-2026-09)
  | "desk"
  // expansion verticals — ADR-0014 manifest variations that REUSE a core pack's screens
  | "coaching"
  | "studio"
  | "turf"
  | "cowork"
  | "spa"
  | "pgHostel"
  | "autoService"
  | "optical"
  | "dentalVet"
  | "derma";

export type Pack = {
  id: PackId;
  label: string;
  /** What the counter actually does all day — shown in the switcher. */
  tagline: string;
  /** Where the rail's home lands. */
  home: string;
  nav: RailItem[];
  /**
   * `core` packs own purpose-built screens. `expansion` packs are ADR-0014 manifest
   * variations: same screens, different vocabulary and nav labels. That is the whole point
   * of the pack manifest — a new vertical should be data, not a new codebase.
   */
  tier: "core" | "expansion";
  /** Which core pack's screens this vertical reuses (expansion only). */
  reuses?: PackId;
  /**
   * Routes this pack owns that do not sit under its {@link Pack.home}.
   *
   * <p>Exists for `/checkout`, which is the restaurant's till and says so in its own file header --
   * "Order and checkout, the restaurant pack's till" -- but lives at a top-level path because it
   * once WAS the front door, back when a restaurant was the only vertical.
   *
   * <p>Every pack's manifest lists a `checkout` screen, so it reads like a shared route, and the POS
   * sweep found what that costs: a gym counter, a salon counter and a hotel front desk that opened
   * `/checkout` were each shown the restaurant's order screen -- Dine-in/Take-away, table T-07,
   * Filter Coffee and Masala Dosa. Only the restaurant rail links there; the others reach it by a
   * typed URL or a stale bookmark, which is exactly what a guard is for.
   *
   * <p>Each vertical does have a checkout -- the gym sells a plan at the door, the salon bills off
   * the calendar, the hotel settles a folio. They are simply not at this path, and pointing them
   * here would be worse than sending them home.
   */
  alsoOwns?: string[];
  /**
   * Tenant vocabulary. The kernel Context carries `vocabulary` for exactly this reason: a
   * coaching centre has students, not customers; a clinic has patients. Screens should read
   * labels from here rather than hard-coding "customer".
   */
  vocabulary?: Record<string, string>;
  /**
   * The order types this pack's till may open, in the order its toggle should offer them.
   *
   * Kernel-declared (`OpenOrderRequest.orderType`, `pos-orchestrator-api.yaml`) and mirrored by
   * `lib/mock/handlers/*.ts`'s `registerOrderOwner` calls, which route a mock `openOrder` by this
   * same value. Declared here rather than only in the mock registry so a live screen — currently
   * `OrderTypeToggle` — has ONE place to read "what can this till open" from; a screen hand-rolling
   * its own list is exactly how the restaurant checkout's toggle and its mock's routing table could
   * silently drift apart.
   */
  orderTypes?: OrderType[];
  /**
   * Which product lockup the rail wears for this pack (DESIGN.md §4, masterplan Q-16).
   *
   * Every till pack wears Perksmith Counter; the Reward Desk is a Perksmith Rewards feature and wears
   * the Rewards lockup inside the same app. Read from this row by `PosRail`, never by comparing a pack
   * id there, and pinned by `packs.desk.test.ts`. Absent means `"counter"`.
   */
  lockup?: "counter" | "rewards";
  /**
   * Capability keys `/pos/context` must report enabled before this pack is shown at all.
   *
   * Mirrors the `capabilities` column of the pack's `tenant.pos_vertical_packs` row. The edge already
   * refuses a context whose manifest lists a denied capability, so this is the till app refusing to
   * show a pack whose fence it cannot see, not the security boundary. Unentitled packs are hidden,
   * never shown disabled (ADR-0014 D9).
   */
  capabilities?: string[];
};

/** Shared tail every vertical gets — the back-office and housekeeping of the counter. */
const SHARED_TAIL: RailItem[] = [
  { id: "customers", label: "Customers", icon: "customers", href: "/customers" },
  { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
  { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
  { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
];

export const PACKS: Pack[] = [
  {
    id: "restaurant",
    tier: "core",
    label: "Restaurant",
    tagline: "Tables, KOT, split bills",
    home: "/restaurant",
    // See Pack.alsoOwns. /checkout is this pack's till, at a top-level path for historical reasons.
    alsoOwns: ["/checkout"],
    orderTypes: ["DINE_IN", "TAKE_AWAY", "DELIVERY"],
    nav: [
      { id: "floor", label: "Floor map", icon: "floor", href: "/restaurant" },
      { id: "orders", label: "Order & checkout", icon: "orders", href: "/checkout" },
      { id: "split", label: "Split · merge", icon: "folio", href: "/restaurant/split" },
      { id: "floorplan", label: "Floor plan", icon: "stock", href: "/restaurant/floorplan" },
      { id: "metrics", label: "Metrics", icon: "reports", href: "/restaurant/metrics" },
      { id: "menu", label: "Menu", icon: "menu", href: "/catalog" },
      ...SHARED_TAIL,
    ],
  },
  {
    id: "retail",
    tier: "core",
    label: "Retail & kirana",
    tagline: "Scan, weigh, settle fast",
    home: "/retail",
    nav: [
      { id: "counter", label: "Fast counter", icon: "scanner", href: "/retail" },
      { id: "menu", label: "Catalog", icon: "stock", href: "/catalog" },
      ...SHARED_TAIL,
    ],
  },
  {
    id: "salon",
    tier: "core",
    label: "Salon & spa",
    tagline: "Chairs, packages, commission",
    home: "/salon",
    nav: [
      { id: "calendar", label: "Day calendar", icon: "calendar", href: "/salon" },
      { id: "book", label: "Self-booking page", icon: "stylist", href: "/book" },
      { id: "menu", label: "Services", icon: "menu", href: "/catalog" },
      ...SHARED_TAIL,
    ],
  },
  {
    id: "clinic",
    tier: "core",
    label: "Clinic",
    tagline: "OPD queue, exempt billing",
    home: "/clinic",
    nav: [
      { id: "queue", label: "OPD queue", icon: "queue", href: "/clinic" },
      { id: "book", label: "Appointments", icon: "calendar", href: "/book" },
      { id: "menu", label: "Services", icon: "menu", href: "/catalog" },
      ...SHARED_TAIL,
    ],
  },
  {
    id: "diagnostics",
    tier: "core",
    label: "Diagnostics lab",
    tagline: "Sample custody, advance billing",
    home: "/diagnostics",
    nav: [
      { id: "samples", label: "Sample board", icon: "samples", href: "/diagnostics" },
      { id: "book", label: "Home collection", icon: "calendar", href: "/book" },
      // The lab's OWN list, not the shared item catalogue: a requisition names tests, and a sample
      // is one test. `/diagnostics/tests` is where that list is kept (and pulled in from the
      // catalogue in one press) — the shared screen cannot write it.
      { id: "menu", label: "Tests & panels", icon: "menu", href: "/diagnostics/tests" },
      ...SHARED_TAIL,
    ],
  },
  {
    id: "gym",
    tier: "core",
    label: "Gym & fitness",
    tagline: "Check-in, classes, renewals",
    home: "/gym",
    nav: [
      { id: "checkin", label: "Door check-in", icon: "scanner", href: "/gym" },
      { id: "classes", label: "Classes", icon: "classes", href: "/gym/classes" },
      { id: "menu", label: "Plans", icon: "menu", href: "/catalog" },
      ...SHARED_TAIL,
    ],
  },
  {
    id: "hotel",
    tier: "core",
    label: "Hotel & lodge",
    tagline: "Front desk, folio, housekeeping",
    home: "/hotel",
    nav: [
      { id: "rooms", label: "Front desk", icon: "rooms", href: "/hotel" },
      { id: "housekeeping", label: "Housekeeping", icon: "housekeeping", href: "/hotel/rooms" },
      { id: "hourly", label: "Day-use grid", icon: "calendar", href: "/hotel/hourly" },
      { id: "menu", label: "Rates & rooms", icon: "menu", href: "/catalog" },
      { id: "setup", label: "Property setup", icon: "stock", href: "/hotel/setup" },
      ...SHARED_TAIL,
    ],
  },
  {
    /*
     * Reward Desk (W6). Not a till: no catalogue, lines, tax, tenders, drawer or shift. The merchant
     * rings the sale on their own POS and the desk records that bill so the customer earns points.
     * So its rail is the three desk screens and Settings -- no Customers, Shift & drawer or Catalog,
     * because a desk holds none of those (reward-desk-pack-plan-2026-09 §2, §4.1).
     */
    id: "desk",
    tier: "core",
    label: "Reward Desk",
    tagline: "Bills from your own POS earn points",
    home: "/desk",
    // The redesigned desk, on its own route until it replaces /desk.
    alsoOwns: ["/desk-v2"],
    lockup: "rewards",
    capabilities: ["desk.rewards"],
    vocabulary: { customer: "Customer", bill: "Bill" },
    nav: [
      { id: "desk_customer", label: "Customer", icon: "customers", href: "/desk" },
      { id: "desk_rewards", label: "Rewards", icon: "folio", href: "/desk/rewards" },
      { id: "desk_today", label: "Today", icon: "reports", href: "/desk/today" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  /* ---------------------------------------------------------------------------
   * EXPANSION VERTICALS — ADR-0014 manifest variations.
   * Each reuses a core pack's screens with its own label, nav wording and vocabulary.
   * Adding a vertical here is a data change: no new screens, no new backend.
   * (Dermatology is the exception — it needs a genuinely new session console.)
   * ------------------------------------------------------------------------- */
  {
    id: "coaching",
    tier: "expansion",
    reuses: "gym",
    label: "Coaching & tuition",
    tagline: "Batches, attendance, fee instalments",
    home: "/gym",
    vocabulary: { customer: "Student", member: "Student", class: "Batch", plan: "Course" },
    nav: [
      { id: "checkin", label: "Attendance", icon: "scanner", href: "/gym" },
      { id: "classes", label: "Batches", icon: "classes", href: "/gym/classes" },
      { id: "menu", label: "Courses & fees", icon: "menu", href: "/catalog" },
      { id: "customers", label: "Students", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "studio",
    tier: "expansion",
    reuses: "gym",
    label: "Yoga & dance studio",
    tagline: "Class packs, memberships, drop-ins",
    home: "/gym",
    vocabulary: { customer: "Member", class: "Session", plan: "Pass" },
    nav: [
      { id: "checkin", label: "Check-in", icon: "scanner", href: "/gym" },
      { id: "classes", label: "Sessions", icon: "classes", href: "/gym/classes" },
      { id: "menu", label: "Passes", icon: "menu", href: "/catalog" },
      { id: "customers", label: "Members", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "turf",
    tier: "expansion",
    reuses: "salon",
    label: "Turf & sports",
    tagline: "Hourly slots, deposits, regulars",
    home: "/salon",
    vocabulary: { customer: "Player", resource: "Turf", appointment: "Slot" },
    nav: [
      { id: "calendar", label: "Day grid", icon: "stylist", href: "/salon" },
      { id: "book", label: "Slot booking", icon: "calendar", href: "/book" },
      { id: "menu", label: "Turfs & rates", icon: "menu", href: "/catalog" },
      { id: "customers", label: "Players", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "cowork",
    tier: "expansion",
    reuses: "gym",
    label: "Co-working",
    tagline: "Seats, meeting rooms, monthly plans",
    home: "/gym",
    vocabulary: { customer: "Member", class: "Meeting room", plan: "Seat plan" },
    nav: [
      { id: "checkin", label: "Door access", icon: "scanner", href: "/gym" },
      { id: "book", label: "Meeting rooms", icon: "calendar", href: "/book" },
      { id: "menu", label: "Seat plans", icon: "menu", href: "/catalog" },
      { id: "customers", label: "Members", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "spa",
    tier: "expansion",
    reuses: "salon",
    label: "Spa & pet grooming",
    tagline: "Therapists, packages, rebooking",
    home: "/salon",
    vocabulary: { customer: "Guest", stylist: "Therapist", service: "Treatment" },
    nav: [
      { id: "calendar", label: "Day calendar", icon: "calendar", href: "/salon" },
      { id: "book", label: "Online booking", icon: "stylist", href: "/book" },
      { id: "menu", label: "Treatments", icon: "menu", href: "/catalog" },
      { id: "customers", label: "Guests", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "pgHostel",
    tier: "expansion",
    reuses: "hotel",
    label: "PG & hostel",
    tagline: "Beds, monthly rent, long stays",
    home: "/hotel",
    vocabulary: { room: "Bed", guest: "Resident", folio: "Rent ledger" },
    nav: [
      { id: "rooms", label: "Beds & residents", icon: "rooms", href: "/hotel" },
      { id: "residents", label: "Rent & dues", icon: "folio", href: "/hotel/residents" },
      { id: "housekeeping", label: "Housekeeping", icon: "housekeeping", href: "/hotel/rooms" },
      { id: "menu", label: "Bed types & rent", icon: "menu", href: "/catalog" },
      { id: "setup", label: "Beds setup", icon: "stock", href: "/hotel/setup" },
      { id: "customers", label: "Residents", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "autoService",
    tier: "expansion",
    reuses: "salon",
    label: "Auto service & wash",
    tagline: "Bays, job cards, prepaid packs",
    home: "/salon",
    // The workshop's own screens: a job card is not an appointment with a longer note, and a vehicle
    // is not a customer. They live at /auto, which this pack owns while still reusing the salon's
    // day grid for its bays.
    alsoOwns: ["/auto"],
    vocabulary: { customer: "Vehicle owner", stylist: "Bay", appointment: "Job card" },
    nav: [
      { id: "jobcards", label: "Job cards", icon: "orders", href: "/auto" },
      { id: "calendar", label: "Bay schedule", icon: "calendar", href: "/salon" },
      { id: "book", label: "Bookings", icon: "stylist", href: "/book" },
      { id: "menu", label: "Services & parts", icon: "menu", href: "/catalog" },
      { id: "vehicles", label: "Vehicles", icon: "customers", href: "/auto/vehicles" },
      { id: "servicedue", label: "Service due", icon: "calendar", href: "/auto/service-due" },
      { id: "bays", label: "Bays & technicians", icon: "stock", href: "/auto/bays" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "optical",
    tier: "expansion",
    reuses: "retail",
    label: "Optical & eyewear",
    tagline: "Prescriptions, made-to-order, fittings",
    home: "/retail",
    vocabulary: { customer: "Patient", item: "Frame / lens" },
    nav: [
      { id: "counter", label: "Counter", icon: "scanner", href: "/retail" },
      { id: "menu", label: "Frames & lenses", icon: "stock", href: "/catalog" },
      { id: "customers", label: "Patients", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "dentalVet",
    tier: "expansion",
    reuses: "clinic",
    label: "Dental & veterinary",
    tagline: "Treatment plans, recalls, multi-visit",
    home: "/clinic",
    vocabulary: { customer: "Patient", token: "Appointment", service: "Procedure" },
    nav: [
      { id: "queue", label: "Today's list", icon: "queue", href: "/clinic" },
      { id: "book", label: "Appointments", icon: "calendar", href: "/book" },
      { id: "menu", label: "Procedures", icon: "menu", href: "/catalog" },
      { id: "customers", label: "Patients", icon: "patients", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
  {
    id: "derma",
    tier: "expansion",
    reuses: "clinic",
    label: "Dermatology & skin",
    tagline: "Multi-session courses, progression photos",
    home: "/derma",
    vocabulary: { customer: "Patient", service: "Treatment", plan: "Course" },
    nav: [
      { id: "sessions", label: "Session console", icon: "patients", href: "/derma" },
      { id: "queue", label: "Today's list", icon: "queue", href: "/clinic" },
      { id: "book", label: "Appointments", icon: "calendar", href: "/book" },
      { id: "menu", label: "Treatments", icon: "menu", href: "/catalog" },
      { id: "customers", label: "Patients", icon: "customers", href: "/customers" },
      { id: "shifts", label: "Shift & drawer", icon: "shifts", href: "/shifts" },
      { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
      { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
    ],
  },
];

export const PACK_BY_ID: Record<PackId, Pack> = Object.fromEntries(
  PACKS.map((p) => [p.id, p]),
) as Record<PackId, Pack>;

/**
 * Which vertical a route belongs to, or undefined when it belongs to all of them.
 *
 * Derived from the packs themselves rather than a second hand-written list: a pack's own routes are
 * the ones under its {@link Pack.home} prefix, so `/hotel/hourly` is the hotel's and `/customers`,
 * `/checkout`, `/reports` and the rest of the shared tail belong to no one in particular and every
 * counter may open them. Adding a vertical therefore adds its guard automatically, which is the only
 * way this stays true — the alternative is a routes-to-packs map that silently stops covering the
 * surface, which is the failure this repository keeps finding.
 *
 * SWEEP-1 S1: nothing consulted the counter's entitlements before rendering a screen, so a hotel
 * front desk could open the salon calendar, the lab's sample board and the restaurant floor, each
 * re-skinning the whole shell. `/pos/context` had been answering the allowed screen list the whole
 * time and no code read it.
 */
export function packOwningRoute(pathname: string): PackId | undefined {
  const path = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  let owner: Pack | undefined;
  for (const pack of PACKS) {
    const claims =
      path === pack.home ||
      path.startsWith(`${pack.home}/`) ||
      (pack.alsoOwns ?? []).some((route) => path === route || path.startsWith(`${route}/`));
    if (!claims) continue;
    if (!owner) {
      owner = pack;
      continue;
    }
    // Longest home wins, so a future "/gym/classes" pack could not be shadowed by "/gym".
    if (pack.home.length !== owner.home.length) {
      if (pack.home.length > owner.home.length) owner = pack;
      continue;
    }
    // Same route, two packs: the CORE pack owns it. Nine of the ten expansion verticals borrow a
    // core pack's home outright -- coaching and cowork both live at /gym -- so naming one of them
    // the owner would lock the others out of a screen they are entitled to. They reach it through
    // `reuses` instead. Derma is the exception that made this rule necessary: it is the only
    // expansion pack with a home of its own, /derma, and before this it belonged to nobody and
    // every counter could open it.
    if (owner.tier !== "core" && pack.tier === "core") owner = pack;
  }
  return owner?.id;
}

/**
 * Whether a counter entitled to `packs` may open a route owned by `owner`.
 *
 * An expansion vertical reuses a core pack's screens (ADR-0014) and its rail points at the host's
 * routes, so a derma counter opening `/clinic` is the design working, not a leak.
 */
export function packsCoverRoute(owner: PackId, packs: PackId[]): boolean {
  return packs.some((pack) => pack === owner || PACK_BY_ID[pack]?.reuses === owner);
}

/** One capability decision as `/pos/context` reports it. */
export type PackEntitlement = { capability: string; enabled: boolean };

/**
 * Whether the counter may show `pack`, given the capability decisions `/pos/context` returned.
 *
 * A pack that declares no capabilities is shown whenever the context lists it, which is how every
 * till pack behaved before the desk. One that declares some -- the desk's `desk.rewards` -- needs
 * every one of them reported and enabled; a missing decision hides it, failing closed.
 */
export function packEntitled(pack: PackId, entitlements: readonly PackEntitlement[] | undefined): boolean {
  const needs = PACK_BY_ID[pack]?.capabilities ?? [];
  return needs.every((key) => (entitlements ?? []).some((e) => e.capability === key && e.enabled));
}

/** The product lockup the rail wears for this pack. */
export function packLockup(pack?: PackId): "counter" | "rewards" {
  return (pack ? PACK_BY_ID[pack]?.lockup : undefined) ?? "counter";
}

export function packNav(pack?: PackId): RailItem[] | undefined {
  return pack ? PACK_BY_ID[pack]?.nav : undefined;
}

/**
 * Owner/back-office surfaces. Not on the counter rail — these belong to the owner console
 * and are reached from Reports.
 */
export const BACK_OFFICE: { id: string; label: string; href: string; blurb: string }[] = [
  { id: "reports", label: "Reports", href: "/reports", blurb: "Sales, payments, staff, loyalty" },
  { id: "burn", label: "Rewards & returns", href: "/burn", blurb: "Put to work vs your reward fund, limit alerts" },
  { id: "group", label: "Group dashboard", href: "/group", blurb: "All branches, league, FX" },
  { id: "org", label: "Org & access", href: "/org", blurb: "Roles, branch scope, audit" },
  { id: "invoice", label: "GST invoice", href: "/invoice", blurb: "Templates, generate, reprint" },
  { id: "accounting", label: "Accounting", href: "/accounting", blurb: "Statement, GSTR-1, exports" },
];

/** Rail glyphs for the back-office destinations, kept beside the list they describe. */
const BACK_OFFICE_ICONS: Record<string, string> = {
  reports: "reports",
  burn: "folio",
  group: "stock",
  org: "customers",
  invoice: "orders",
  accounting: "queue",
};

/**
 * The back-office group as rail items.
 *
 * Derived from BACK_OFFICE so there is ONE list of these six destinations, not
 * two that drift. POS-8 owns it and registers all six now -- including the four
 * whose screens land with POS-10..14 -- because the alternative is five later
 * stories each editing this file.
 *
 * Visibility is decided by the SERVER-provided staff role, and the back-office
 * endpoints enforce their own permissions regardless: hiding the group is a
 * courtesy to the cashier, never the security boundary.
 */
export const BACK_OFFICE_RAIL: RailItem[] = BACK_OFFICE.map((b) => ({
  id: b.id,
  label: b.label,
  icon: BACK_OFFICE_ICONS[b.id] ?? "reports",
  href: b.href,
}));


/**
 * Roles the tenant's own context reports for someone who operates the business
 * rather than the till. Anything else -- notably CASHIER -- does not see the
 * group. A role the server adds later is treated as counter-only until it is
 * listed here, which fails closed.
 */
const BACK_OFFICE_ROLES = new Set(["BRANCH_MANAGER", "TENANT_ADMIN", "OWNER", "SUPERADMIN"]);

export function canSeeBackOffice(role?: string): boolean {
  return role !== undefined && BACK_OFFICE_ROLES.has(role);
}

/**
 * Counter-operations nav — for the vertical-agnostic screens (customers, catalog, shift,
 * settings). A tenant reaches these from any pack, so the nav stays neutral and every item
 * resolves to a real route. *
 * <p>"every item resolves to a real route" was false until 7 September: this entry and OWNER_NAV's
 * pointed at `/screens`, and there is no `app/screens/`. Both 404'd, from five back-office screens,
 * in every vertical, and Next prefetched the miss on hover. `/` is the right destination and always
 * was — it is the resolver that sends an operator back to whichever counter their session is scoped
 * to. `check-counter-scope.mjs` now asserts every rail href resolves, so the sentence above is
 * checked rather than merely written.
 */
export const OPS_NAV: RailItem[] = [
  { id: "home", label: "All screens", icon: "floor", href: "/" },
  { id: "menu", label: "Catalog", icon: "menu", href: "/catalog" },
  ...SHARED_TAIL,
];

/** Owner console nav — the back-office surfaces, plus a way back to the counter. */
export const OWNER_NAV: RailItem[] = [
  { id: "home", label: "Back to counter", icon: "floor", href: "/" },
  { id: "reports", label: "Reports", icon: "reports", href: "/reports" },
  { id: "burn", label: "Rewards & returns", icon: "folio", href: "/burn" },
  { id: "group", label: "Group dashboard", icon: "stock", href: "/group" },
  { id: "org", label: "Org & access", icon: "customers", href: "/org" },
  { id: "invoice", label: "GST invoice", icon: "orders", href: "/invoice" },
  { id: "accounting", label: "Accounting", icon: "queue", href: "/accounting" },
  { id: "settings", label: "Settings", icon: "settings", href: "/settings" },
];

/** One step in a vertical's counter journey. */
export type FlowStep = {
  route: string;
  title: string;
  /** What the operator is actually doing here — the reason the step exists. */
  does: string;
  /** Mock number in docs/design/pos-mocks, when the step came from one. */
  mock?: string;
};

/**
 * The counter journey per vertical, in the order a shift actually runs: sign in → do the
 * work → handle the exceptions → close the drawer.
 *
 * This is what `/screens?pack=<id>` walks you through. It exists because reviewing a POS
 * screen-by-screen tells you nothing about whether the *flow* holds together.
 */
export const FLOWS: Record<PackId, FlowStep[]> = {
  restaurant: [
    { route: "/counter", title: "Sign in at the counter", does: "Cashier opens the till for a location" },
    { route: "/restaurant", title: "Floor map", does: "See tables, reservations and the walk-in waitlist", mock: "13" },
    { route: "/", title: "Order & checkout", does: "Take the order, apply loyalty, settle the bill", mock: "1" },
    { route: "/restaurant/split", title: "Split · merge · transfer", does: "Guests pay separately, or move a table", mock: "25" },
    { route: "/restaurant/floorplan", title: "Floor plan editor", does: "Rearrange the room; owner-side setup", mock: "31" },
    { route: "/xcut", title: "Edge cases", does: "Approvals, refunds, tender failures, offline", mock: "22–28" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  retail: [
    { route: "/counter", title: "Sign in at the counter", does: "Cashier opens the till" },
    { route: "/retail", title: "Fast counter", does: "Scan, weigh, settle — the whole job", mock: "18" },
    { route: "/catalog", title: "Catalog & stock", does: "Prices, GST slabs, low-stock" },
    { route: "/xcut", title: "Edge cases", does: "Returns, tender failures, offline", mock: "22–28" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  salon: [
    { route: "/counter", title: "Sign in at the counter", does: "Front desk opens the day" },
    { route: "/book", title: "Guest books online", does: "Public self-booking page (mobile-first)", mock: "19" },
    { route: "/salon", title: "Stylist day calendar", does: "Chairs, appointments, chair-side checkout", mock: "14" },
    { route: "/customers", title: "Guest & loyalty", does: "Packages, points, visit history" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  clinic: [
    { route: "/counter", title: "Sign in at the counter", does: "Front desk opens the OPD" },
    { route: "/clinic", title: "OPD token queue", does: "Call patients, EMR peek, exempt billing, TPA", mock: "15" },
    { route: "/customers", title: "Patient records", does: "History and loyalty (restricted, audited)" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  diagnostics: [
    { route: "/counter", title: "Sign in at the counter", does: "Lab reception opens" },
    { route: "/diagnostics", title: "Sample custody board", does: "Requisition → collect → process → report", mock: "16" },
    { route: "/book", title: "Home collection slot", does: "Assign a phlebotomist visit", mock: "19" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  gym: [
    { route: "/counter", title: "Sign in at the counter", does: "Front desk opens" },
    { route: "/gym", title: "Door check-in", does: "Allow or block entry, classes, renewals", mock: "17" },
    { route: "/customers", title: "Member & loyalty", does: "Plan status, streak, points" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  hotel: [
    { route: "/counter", title: "Sign in at the counter", does: "Front desk opens the shift" },
    { route: "/hotel", title: "Front desk & folio", does: "Arrivals, departures, the running stay bill", mock: "12" },
    { route: "/hotel/rooms", title: "Housekeeping board", does: "Turn rooms around, release to sellable", mock: "20" },
    { route: "/hotel/hourly", title: "Day-use grid", does: "Hourly bookings; catch overlaps", mock: "21" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  desk: [
    { route: "/counter", title: "Sign in at the desk", does: "Open the Reward Desk for the day" },
    { route: "/desk", title: "Find the customer", does: "Look them up by phone, see their points" },
    { route: "/desk/rewards", title: "Enter the bill", does: "Bill number and total from your own POS; they earn points" },
    { route: "/desk/today", title: "Today's bills", does: "Match against your POS day-end; reverse a mistake" },
  ],
  /* expansion verticals — same journey shape as the pack they reuse, their own words */
  coaching: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the centre for the day" },
    { route: "/gym", title: "Mark attendance", does: "Students check in; batches and capacity", mock: "17" },
    { route: "/customers", title: "Student & fees", does: "Instalments, dues, loyalty" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  studio: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the studio" },
    { route: "/gym", title: "Session check-in", does: "Passes, drop-ins, class capacity", mock: "17" },
    { route: "/customers", title: "Member & passes", does: "Pass balance, points" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  turf: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the facility" },
    { route: "/book", title: "Player books a slot", does: "Public hourly booking with deposit", mock: "19" },
    { route: "/salon", title: "Day grid", does: "Turf-by-hour schedule; spot conflicts", mock: "14" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  cowork: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the space" },
    { route: "/gym", title: "Door access", does: "Seat plan decides entry, like a membership", mock: "17" },
    { route: "/book", title: "Meeting rooms", does: "Hourly room booking", mock: "19" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  spa: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the day" },
    { route: "/book", title: "Guest books online", does: "Public self-booking", mock: "19" },
    { route: "/salon", title: "Therapist calendar", does: "Rooms, packages, chair-side checkout", mock: "14" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  pgHostel: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the shift" },
    { route: "/hotel", title: "Beds & residents", does: "Occupancy and the monthly rent ledger", mock: "12" },
    { route: "/hotel/rooms", title: "Housekeeping board", does: "Turn beds around", mock: "20" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  autoService: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the workshop" },
    { route: "/book", title: "Owner books a slot", does: "Bay booking with deposit", mock: "19" },
    { route: "/salon", title: "Bay schedule", does: "Job cards across bays, like chairs", mock: "14" },
    { route: "/retail", title: "Parts & consumables", does: "Add parts to the job card", mock: "18" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  optical: [
    { route: "/counter", title: "Sign in at the counter", does: "Open the store" },
    { route: "/retail", title: "Counter & fitting", does: "Frames, lenses, made-to-order", mock: "18" },
    { route: "/customers", title: "Patient & prescription", does: "Rx history, recall" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  dentalVet: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the practice" },
    { route: "/clinic", title: "Today's list", does: "Appointments, records, billing", mock: "15" },
    { route: "/customers", title: "Patient & recalls", does: "Treatment plan across visits" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
  derma: [
    { route: "/counter", title: "Sign in at the front desk", does: "Open the clinic" },
    { route: "/derma", title: "Session console", does: "Sitting N of M: photos, params, draw-down", mock: "36" },
    { route: "/clinic", title: "Today's list", does: "Queue and billing", mock: "15" },
    { route: "/customers", title: "Patient & course", does: "Course balance, loyalty" },
    { route: "/shifts", title: "Close the drawer", does: "Declare cash, reconcile variance", mock: "24" },
  ],
};
